import { createChart, AreaSeries, CrosshairMode, LineStyle, LastPriceAnimationMode } from 'lightweight-charts';

const RED = '#e74c3c';
const BLUE = '#3498db';
const GRAY = '#9e9e9e';
const GREEN = '#27ae60';

function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

function round(v, decimals = 1) {
  const f = 10 ** decimals;
  return (Math.round(v * f) / f).toFixed(decimals);
}

function timeToDate(time) {
  if (typeof time === 'number') return new Date(time * 1000);
  if (typeof time === 'string') return new Date(time);
  if (time && typeof time === 'object' && time.year) {
    return new Date(Date.UTC(time.year, (time.month || 1) - 1, time.day || 1));
  }
  return new Date(time);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(d) {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function fmtDateTime(d) {
  return `${fmtDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtLive(d) {
  return `${fmtDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fmtHM(d, tickMarkType) {
  if (tickMarkType === 2) return fmtDate(d);
  return fmtDateTime(d);
}

function fmtDay(d, tickMarkType) {
  if (tickMarkType === 2 || (d.getHours() === 0 && d.getMinutes() === 0)) {
    return fmtDate(d);
  }
  return fmtDateTime(d);
}

function fmtWeek(d, tickMarkType) {
  if (tickMarkType === 2) return fmtDate(d);
  return fmtDateTime(d);
}

function fmtMonth(d) {
  return fmtDate(d);
}

function fmtYear(d) {
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtCustom(d, tickMarkType) {
  if (tickMarkType === 2) return fmtDate(d);
  return fmtDateTime(d);
}

const FMT = {
  live: fmtLive,
  hour: fmtHM,
  day: fmtDay,
  week: fmtWeek,
  month: fmtMonth,
  year: fmtYear,
  custom: fmtCustom,
};

/** Map web time-range selector to axis formatting. Dates stay visible on every range. */
export function getAxisFormat(timeRange) {
  switch (timeRange) {
    case '1h':
      return { timeVisible: true, secondsVisible: false, tickMarkFormatter: 'hour' };
    case '6h':
      return { timeVisible: true, secondsVisible: false, tickMarkFormatter: 'day' };
    case '24h':
      return { timeVisible: true, secondsVisible: false, tickMarkFormatter: 'day' };
    case '7d':
      return { timeVisible: true, secondsVisible: false, tickMarkFormatter: 'week' };
    case '30d':
      return { timeVisible: true, secondsVisible: false, tickMarkFormatter: 'month' };
    default:
      return { timeVisible: true, secondsVisible: false, tickMarkFormatter: 'custom' };
  }
}

function buildPoints(data, thresholdMin, thresholdMax) {
  const hasThreshold = thresholdMin != null || thresholdMax != null;
  const pointColor = (v) => {
    if (!hasThreshold) return undefined;
    if (thresholdMax != null && v > thresholdMax) return RED;
    if (thresholdMin != null && v < thresholdMin) return BLUE;
    return GRAY;
  };

  return (data || [])
    .filter((d) => d.date && d.value != null && !Number.isNaN(Number(d.value)))
    .map((d) => {
      const value = Number(d.value);
      const time = Math.floor(new Date(d.date).getTime() / 1000);
      const color = pointColor(value);
      return color ? { time, value, color } : { time, value };
    })
    .sort((a, b) => a.time - b.time)
    .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time);
}

/**
 * Create a Lightweight Charts instance matching the native SensorPush chart styling.
 * Returns { update, resize, destroy }.
 */
export function createLightweightSensorChart(container, initialOptions = {}) {
  let chart = null;
  let series = null;
  let seriesType = null;
  let zoneSeries = { red: null, blue: null, gray: null };
  let priceLines = [];
  let tooltipEl = null;
  let dataTimeMin = null;
  let dataTimeMax = null;
  let lastPayload = null;
  let tooltipPinned = false;

  const makeTickFormatter = (fmtName) => {
    const fn = FMT[fmtName] || fmtCustom;
    return (time, tickMarkType) => {
      try {
        const isDayBoundary = tickMarkType === 2;
        if (typeof time === 'number' && dataTimeMin !== null && dataTimeMax !== null) {
          if (!isDayBoundary && (time < dataTimeMin || time > dataTimeMax)) return '';
        }
        const s = fn(timeToDate(time), tickMarkType);
        return s == null ? '' : s;
      } catch {
        return '';
      }
    };
  };

  const makeAreaSeries = (c) =>
    chart.addSeries(AreaSeries, {
      lineColor: c,
      topColor: `${c}55`,
      bottomColor: `${c}0D`,
      lineWidth: 2,
      lineType: 0,
      crosshairMarkerVisible: false,
      priceScaleId: 'left',
      lastPriceAnimation: LastPriceAnimationMode.Continuous,
    });

  const teardownAllSeries = () => {
    if (series) {
      try { chart.removeSeries(series); } catch { /* ignore */ }
      series = null;
    }
    ['red', 'blue', 'gray'].forEach((k) => {
      if (zoneSeries[k]) {
        try { chart.removeSeries(zoneSeries[k]); } catch { /* ignore */ }
        zoneSeries[k] = null;
      }
    });
    priceLines = [];
  };

  const ensureSeries = (thresholdActive, color) => {
    const wantType = thresholdActive ? 'multi' : 'area';
    if (wantType === seriesType) return;
    teardownAllSeries();
    if (wantType === 'multi') {
      zoneSeries.red = makeAreaSeries(RED);
      zoneSeries.blue = makeAreaSeries(BLUE);
      zoneSeries.gray = makeAreaSeries(GRAY);
    } else {
      series = makeAreaSeries(color);
    }
    seriesType = wantType;
  };

  const ensureTooltip = () => {
    if (tooltipEl) return;
    tooltipEl = document.createElement('div');
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.zIndex = '1000';
    tooltipEl.style.padding = '8px 10px';
    tooltipEl.style.background = 'rgba(0,0,0,0.9)';
    tooltipEl.style.color = '#fff';
    tooltipEl.style.fontFamily = '-apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", Roboto, sans-serif';
    tooltipEl.style.fontSize = '12px';
    tooltipEl.style.fontWeight = '500';
    tooltipEl.style.lineHeight = '1.4';
    tooltipEl.style.borderRadius = '0';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.display = 'none';
    tooltipEl.style.whiteSpace = 'nowrap';
    container.style.position = 'relative';
    container.appendChild(tooltipEl);
  };

  const showTooltip = (param, payload) => {
    if (!param?.time || !param.seriesData?.size) {
      tooltipEl.style.display = 'none';
      return false;
    }
    let pt = null;
    if (series) {
      pt = param.seriesData.get(series);
    } else {
      ['red', 'blue', 'gray'].some((k) => {
        if (zoneSeries[k] && param.seriesData.has(zoneSeries[k])) {
          pt = param.seriesData.get(zoneSeries[k]);
          return true;
        }
        return false;
      });
    }
    if (!pt || pt.value === undefined) {
      tooltipEl.style.display = 'none';
      return false;
    }
    const d = timeToDate(param.time);
    const dateStr = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const unit = payload.unit || '';
    tooltipEl.innerHTML =
      `<div style="opacity:0.7">${dateStr} ${timeStr}</div>` +
      `<div>${payload.title}: ${round(pt.value, 1)}${unit}</div>`;
    tooltipEl.style.display = 'block';
    const rect = container.getBoundingClientRect();
    const x = typeof param.point?.x === 'number' ? param.point.x : 0;
    const y = typeof param.point?.y === 'number' ? param.point.y : 0;
    const width = payload.width || rect.width || 300;
    tooltipEl.style.left = `${Math.min(Math.max(x + 12, 4), width - 120)}px`;
    tooltipEl.style.top = `${Math.max(y - 40, 4)}px`;
    tooltipEl.style.borderLeft = `3px solid ${payload.color || RED}`;
    return true;
  };

  const ensureChart = (payload) => {
    const theme = payload.theme || {};
    const bg = theme.backgroundColor || 'transparent';
    const textColor = theme.textColor || '#666';
    const axisLine = theme.axisLineColor || '#ccc';
    const splitLine = theme.splitLineColor || '#e0e0e0';
    const fmtName = payload.axis?.tickMarkFormatter || 'custom';

    if (chart) {
      chart.applyOptions({
        layout: {
          background: { type: 'solid', color: bg === 'transparent' ? '#00000000' : bg },
          textColor,
          attributionLogo: false,
        },
        leftPriceScale: { visible: true, borderColor: axisLine, entireTextOnly: true },
        rightPriceScale: { visible: false },
        timeScale: {
          borderColor: axisLine,
          timeVisible: payload.axis.timeVisible,
          secondsVisible: payload.axis.secondsVisible,
          tickMarkFormatter: makeTickFormatter(fmtName),
          minHeight: 40,
        },
        grid: {
          vertLines: { color: splitLine },
          horzLines: { color: splitLine },
        },
        crosshair: { mode: CrosshairMode.Normal },
      });
      return;
    }

    chart = createChart(container, {
      width: payload.width || container.clientWidth || 300,
      height: payload.height || 280,
      layout: {
        background: { type: 'solid', color: bg === 'transparent' ? '#00000000' : bg },
        textColor,
        fontSize: 10,
        fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", Roboto, sans-serif',
        attributionLogo: false,
      },
      leftPriceScale: { visible: true, borderColor: axisLine, entireTextOnly: true },
      rightPriceScale: { visible: false },
      timeScale: {
        borderColor: axisLine,
        timeVisible: payload.axis.timeVisible,
        secondsVisible: payload.axis.secondsVisible,
        tickMarkFormatter: makeTickFormatter(fmtName),
        fixLeftEdge: true,
        fixRightEdge: true,
        minHeight: 40,
      },
      grid: {
        vertLines: { color: splitLine },
        horzLines: { color: splitLine },
      },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: false,
      handleScale: false,
    });

    ensureTooltip();
    ensureSeries(!!payload.thresholdColors, payload.color);

    chart.subscribeCrosshairMove((param) => {
      if (tooltipPinned) {
        showTooltip(param, lastPayload);
      } else if (param?.point) {
        showTooltip(param, lastPayload);
      } else {
        tooltipEl.style.display = 'none';
      }
    });

    container.addEventListener('click', (e) => {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (tooltipPinned) {
        tooltipPinned = false;
        chart.clearCrosshairPosition();
        tooltipEl.style.display = 'none';
        return;
      }
      const timeScale = chart.timeScale();
      let time = timeScale.coordinateToTime(x);
      if ((time == null) && lastPayload?.points?.length) {
        const visibleRange = timeScale.getVisibleRange();
        if (visibleRange) {
          time = x < (lastPayload.width || rect.width) / 2 ? visibleRange.from : visibleRange.to;
        }
      }
      if (time == null) return;
      const sHost = series || zoneSeries.gray || zoneSeries.red || zoneSeries.blue;
      if (!sHost) return;
      let price = 0;
      try { price = sHost.coordinateToPrice(y); } catch { /* ignore */ }
      tooltipPinned = true;
      chart.setCrosshairPosition(price ?? 0, time, sHost);
    });
  };

  const render = (payload) => {
    lastPayload = payload;
    ensureChart(payload);
    tooltipPinned = false;
    try { chart.clearCrosshairPosition(); } catch { /* ignore */ }
    if (tooltipEl) tooltipEl.style.display = 'none';

    if (payload.width && payload.height) {
      chart.resize(payload.width, payload.height);
    }

    ensureSeries(!!payload.thresholdColors, payload.color);
    if (!series && !zoneSeries.gray) return;

    if (seriesType === 'multi') {
      const redPts = [];
      const bluePts = [];
      const grayPts = [];
      payload.points.forEach((p) => {
        const d = { time: p.time, value: p.value };
        if (p.color === RED) redPts.push(d);
        else if (p.color === BLUE) bluePts.push(d);
        else grayPts.push(d);
      });
      zoneSeries.red?.setData(redPts);
      zoneSeries.blue?.setData(bluePts);
      zoneSeries.gray?.setData(grayPts);
    } else if (series) {
      series.setData(payload.points.map((p) => ({ time: p.time, value: p.value })));
    }

    const priceLineHost = series || zoneSeries.gray;
    if (priceLines.length) {
      priceLines.forEach((pl) => {
        try { priceLineHost?.removePriceLine(pl); } catch { /* ignore */ }
      });
      priceLines = [];
    }

    if (payload.threshold && priceLineHost) {
      const t = payload.threshold;
      if (t.max != null) {
        priceLines.push(priceLineHost.createPriceLine({
          price: t.max,
          color: t.red || RED,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: 'max',
        }));
      }
      if (t.min != null) {
        priceLines.push(priceLineHost.createPriceLine({
          price: t.min,
          color: t.blue || BLUE,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: 'min',
        }));
      }
      if (t.target != null) {
        priceLines.push(priceLineHost.createPriceLine({
          price: t.target,
          color: t.green || GREEN,
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          axisLabelVisible: true,
          title: 'target',
        }));
      }
    }

    if (payload.statsLines && priceLineHost) {
      const sl = payload.statsLines;
      const existingThrVals = [];
      const thrSrc = payload.threshold || payload.thresholdColors || {};
      if (thrSrc.min != null) existingThrVals.push(thrSrc.min);
      if (thrSrc.max != null) existingThrVals.push(thrSrc.max);
      if (thrSrc.target != null) existingThrVals.push(thrSrc.target);

      if (sl.min != null && !existingThrVals.includes(sl.min)) {
        priceLines.push(priceLineHost.createPriceLine({
          price: sl.min,
          color: GREEN,
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: 'min',
        }));
      }
      if (sl.max != null && !existingThrVals.includes(sl.max)) {
        priceLines.push(priceLineHost.createPriceLine({
          price: sl.max,
          color: RED,
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: 'max',
        }));
      }
      if (sl.avg != null && !existingThrVals.includes(sl.avg)) {
        priceLines.push(priceLineHost.createPriceLine({
          price: sl.avg,
          color: GRAY,
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: 'avg',
        }));
      }
    }

    if (payload.points.length) {
      dataTimeMin = payload.points[0].time;
      dataTimeMax = payload.points[payload.points.length - 1].time;
    } else {
      dataTimeMin = null;
      dataTimeMax = null;
    }

    if (payload.points.length >= 2) {
      try {
        chart.timeScale().setVisibleRange({
          from: payload.points[0].time,
          to: payload.points[payload.points.length - 1].time,
        });
      } catch {
        chart.timeScale().fitContent();
      }
    } else {
      chart.timeScale().fitContent();
    }

    const vals = payload.points.map((p) => p.value);
    if (vals.length) {
      const scaleHost = series || zoneSeries.gray || zoneSeries.red || zoneSeries.blue;
      scaleHost?.priceScale().applyOptions({
        autoScale: true,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      });
    }
  };

  const buildPayload = (options) => {
    const {
      data,
      label,
      unit,
      color,
      timeRange,
      isDark,
      width,
      height,
      thresholdMin,
      thresholdMax,
      thresholdTarget,
      showThresholdLines = true,
      statsMin,
      statsMax,
      statsAvg,
    } = options;

    const hasThreshold = thresholdMin != null || thresholdMax != null || thresholdTarget != null;
    const points = buildPoints(data, thresholdMin, thresholdMax);
    const axis = getAxisFormat(timeRange);

    return {
      points,
      color: color || RED,
      unit: unit || '',
      title: label || '',
      width,
      height,
      axis,
      thresholdColors: hasThreshold
        ? { min: thresholdMin, max: thresholdMax, red: RED, blue: BLUE, gray: GRAY }
        : null,
      threshold: hasThreshold && showThresholdLines
        ? { min: thresholdMin, max: thresholdMax, target: thresholdTarget, red: RED, blue: BLUE, gray: GRAY, green: GREEN }
        : null,
      statsLines: showThresholdLines && (statsMin != null || statsMax != null || statsAvg != null)
        ? { min: statsMin, max: statsMax, avg: statsAvg }
        : null,
      theme: {
        backgroundColor: 'transparent',
        textColor: isDark ? '#c6c6c6' : '#666',
        axisLineColor: isDark ? '#525252' : '#ccc',
        axisLabelColor: isDark ? '#c6c6c6' : '#666',
        splitLineColor: isDark ? '#393939' : '#e0e0e0',
      },
    };
  };

  const api = {
    update(options) {
      render(buildPayload(options));
    },
    resize(width, height) {
      if (chart && width && height) {
        chart.resize(width, height);
        if (lastPayload) {
          lastPayload.width = width;
          lastPayload.height = height;
        }
      }
    },
    destroy() {
      if (chart) {
        chart.remove();
        chart = null;
      }
      if (tooltipEl?.parentNode) {
        tooltipEl.parentNode.removeChild(tooltipEl);
      }
      tooltipEl = null;
      series = null;
      zoneSeries = { red: null, blue: null, gray: null };
      seriesType = null;
      priceLines = [];
    },
  };

  if (initialOptions.data?.length) {
    api.update(initialOptions);
  }

  return api;
}

export const TEMP_COLOR = RED;
export const HUMIDITY_COLOR = '#2ecc71';
